import * as THREE from 'three';
import type { Terrain, SurfaceObstacle } from '../../surface';
import { createMatteMaterial } from '../../../visual';
import type { FaunaSpec } from '../BiomeProfile';
import type { PlayerStats } from '../../player';
import { Creature } from './Creature';
import type { CreatureColors } from './builders';

export interface FaunaSpawnOptions {
  /** Playable radius — fauna spawns inside it. */
  readonly spread: number;
  /** Don't spawn within this distance of the spawn pad. */
  readonly excludeRadius?: number;
  /** Astronaut position provider — sampled by each creature. */
  readonly getPlayerPosition: () => THREE.Vector3;
  /** Boundary clamp shared with the FPS controller. */
  readonly boundaryRadius: number;
  /** Optional inner-ring spawn fraction. Defaults to 0 — distribution is
   *  uniform-area across the disc. */
  readonly innerRingFraction?: number;
  /** Outer edge of the inner ring (inclusive). */
  readonly innerRingMax?: number;
  /** Player stats for hostile-melee damage. */
  readonly stats?: PlayerStats | null;
  /** Player collision radius — passed through to creatures. Default 0.36
   *  (matches Astronaut). */
  readonly playerRadius?: number;
}

export interface FaunaSpawnResult {
  readonly creatures: readonly Creature[];
  /** Live obstacles, one per creature, with shared `position` references
   *  so they track the creature movement automatically. Push these into
   *  the surface scene's obstacle list so the player can't walk through
   *  fauna. */
  readonly obstacles: readonly SurfaceObstacle[];
}

/**
 * Spawn fauna across a planet's playable disc per spec. Creatures are
 * `Entity` subclasses ticked by the scene; their obstacle entries are
 * exposed so the FPS controller can resolve the player against them.
 */
export function spawnFauna(
  terrain: Terrain,
  specs: readonly FaunaSpec[],
  options: FaunaSpawnOptions
): FaunaSpawnResult {
  const creatures: Creature[] = [];
  const obstacles: SurfaceObstacle[] = [];
  const exclude = options.excludeRadius ?? 0;
  const excludeSq = exclude * exclude;
  const innerRingFraction = options.innerRingFraction ?? 0;
  const innerRingMax = options.innerRingMax ?? 160;

  for (const spec of specs) {
    const colors = buildSpeciesMaterials(spec);
    const innerTarget = Math.floor(spec.count * innerRingFraction);
    let innerPlaced = 0;
    let placed = 0;
    let attempts = 0;
    const maxAttempts = spec.count * 8;
    while (placed < spec.count && attempts++ < maxAttempts) {
      const inInner = innerPlaced < innerTarget;
      const angle = Math.random() * Math.PI * 2;
      let r: number;
      if (inInner) {
        const minR = Math.max(exclude, 8);
        const maxR = innerRingMax;
        const minR2 = minR * minR;
        const maxR2 = maxR * maxR;
        r = Math.sqrt(minR2 + Math.random() * (maxR2 - minR2));
      } else {
        r = Math.sqrt(Math.random()) * options.spread;
      }
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      if (x * x + z * z < excludeSq) continue;

      const y = terrain.getCollisionGroundY(x, z);
      const c = new Creature({
        spec,
        position: new THREE.Vector3(x, y, z),
        terrain,
        getPlayerPosition: options.getPlayerPosition,
        boundaryRadius: options.boundaryRadius,
        colors,
        stats: options.stats ?? null,
        playerRadius: options.playerRadius
      });
      creatures.push(c);
      obstacles.push(c.obstacle);
      placed++;
      if (inInner) innerPlaced++;
    }
  }

  return { creatures, obstacles };
}

function buildSpeciesMaterials(spec: FaunaSpec): CreatureColors {
  const bodyMat = createMatteMaterial({
    color: spec.bodyTint,
    roughness: 0.7,
    flatShading: false
  });
  const accentMat = createMatteMaterial({
    color: spec.accent,
    roughness: 0.55,
    flatShading: false
  });
  let emissiveMat: THREE.MeshStandardMaterial | null = null;
  if (spec.emissive !== undefined) {
    emissiveMat = createMatteMaterial({
      color: spec.emissive,
      roughness: 0.45,
      emissive: spec.emissive,
      emissiveIntensity: spec.emissiveStrength ?? 0.5,
      flatShading: false
    });
    emissiveMat.toneMapped = false;
  }
  return { bodyMat, accentMat, emissiveMat };
}
